import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogoMark } from './ui';
import { iniciais } from '../lib/format';
import {
  LayoutDashboard, ShoppingBag, PackagePlus, Users, Scissors,
  Wallet, Zap, LogOut, Menu, PanelLeftClose, PanelLeftOpen,
  Store, ExternalLink, SlidersHorizontal,
} from 'lucide-react';

const secoes = [
  {
    titulo: 'Operação',
    itens: [
      { path: '/', label: 'Central', icon: LayoutDashboard },
      { path: '/pedidos', label: 'Pedidos', icon: ShoppingBag },
      { path: '/pedidos/novo', label: 'Novo Pedido', icon: PackagePlus },
      { path: '/entrada-rapida', label: 'Entrada Rápida', icon: Zap },
    ],
  },
  {
    titulo: 'Produção',
    itens: [
      { path: '/costura', label: 'Costura', icon: Scissors },
    ],
  },
  {
    titulo: 'Gestão',
    itens: [
      { path: '/clientes', label: 'Clientes', icon: Users },
      { path: '/financeiro', label: 'Financeiro', icon: Wallet },
      { path: '/itens-padrao', label: 'Itens Padrão', icon: SlidersHorizontal },
    ],
  },
  {
    titulo: 'Loja Virtual',
    itens: [
      { path: '/catalogo', label: 'Catálogo', icon: Store },
      { path: '/loja', label: 'Ver Loja', icon: ExternalLink, externo: true },
    ],
  },
];

const titulosRota: Record<string, string> = {
  '/': 'Central de Operações',
  '/pedidos': 'Pedidos',
  '/pedidos/novo': 'Novo Pedido',
  '/clientes': 'Clientes',
  '/costura': 'Costura',
  '/financeiro': 'Financeiro',
  '/entrada-rapida': 'Entrada Rápida',
  '/catalogo': 'Catálogo da Loja',
  '/itens-padrao': 'Itens Padrão',
};

export function Layout() {
  const { usuario, logout } = useAuth();
  const location = useLocation();
  const [recolhida, setRecolhida] = useState(false);
  const [menuMobile, setMenuMobile] = useState(false);

  useEffect(() => {
    setMenuMobile(false);
  }, [location.pathname]);

  const tituloPagina = titulosRota[location.pathname] || 'MONCHERY OS';

  const sidebar = (
    <div className="flex flex-col h-full bg-navy-950 text-ink-300">
      {/* Logo */}
      <div className={`h-16 flex items-center border-b border-white/[0.06] ${recolhida ? 'justify-center px-2' : 'px-5'}`}>
        <Link to="/" className="flex items-center gap-3 min-w-0">
          <LogoMark size={34} />
          {!recolhida && (
            <div className="min-w-0 leading-none">
              <p className="font-display font-bold text-[15px] text-white tracking-tight">MONCHERY</p>
              <p className="text-2xs font-semibold text-brand-300 tracking-[0.18em] mt-1">OS · MALHARIA</p>
            </div>
          )}
        </Link>
      </div>

      {/* Navegação */}
      <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-6">
        {secoes.map((secao) => (
          <div key={secao.titulo}>
            {!recolhida && (
              <p className="px-3 mb-2 text-2xs font-semibold uppercase tracking-[0.14em] text-ink-500">
                {secao.titulo}
              </p>
            )}
            <div className="space-y-1">
              {secao.itens.map((item: any) => {
                const Icon = item.icon;
                const ativo = location.pathname === item.path;
                const classes = `relative flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150 group ${
                  recolhida ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5'
                } ${
                  ativo
                    ? 'bg-brand-600/15 text-white'
                    : 'text-ink-400 hover:text-white hover:bg-white/[0.05]'
                }`;
                const conteudo = (
                  <>
                    {ativo && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-brand-500" />
                    )}
                    <Icon className={`w-[18px] h-[18px] shrink-0 transition-colors ${ativo ? 'text-brand-400' : 'text-ink-500 group-hover:text-ink-200'}`} />
                    {!recolhida && <span className="truncate">{item.label}</span>}
                  </>
                );
                if (item.externo) {
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noreferrer"
                      title={recolhida ? item.label : undefined}
                      className={classes}
                    >
                      {conteudo}
                    </a>
                  );
                }
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={recolhida ? item.label : undefined}
                    className={classes}
                  >
                    {conteudo}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Rodapé: usuário */}
      <div className="border-t border-white/[0.06] p-3 space-y-1">
        {!recolhida && usuario && (
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-navy-800 flex items-center justify-center text-xs font-bold text-white shrink-0">
              {iniciais(usuario.nome)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-white truncate">{usuario.nome}</p>
              <p className="text-2xs text-ink-500 truncate">{usuario.email}</p>
            </div>
          </div>
        )}
        <div className={`flex ${recolhida ? 'flex-col' : 'flex-row'} gap-1`}>
          <button
            onClick={() => setRecolhida(!recolhida)}
            className="hidden lg:flex flex-1 items-center justify-center gap-2 px-3 py-2 rounded-lg text-2xs font-semibold text-ink-500 hover:text-white hover:bg-white/[0.05] transition-colors"
          >
            {recolhida ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            {!recolhida && <span>Recolher menu</span>}
          </button>
          <button
            onClick={logout}
            title="Sair"
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-2xs font-semibold text-ink-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            {!recolhida && <span>Sair</span>}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-ink-50 overflow-hidden">
      {/* Sidebar desktop */}
      <aside
        className={`hidden lg:block shrink-0 transition-all duration-300 ease-in-out ${
          recolhida ? 'w-[76px]' : 'w-[264px]'
        }`}
      >
        {sidebar}
      </aside>

      {/* Sidebar mobile (overlay) */}
      {menuMobile && (
        <div className="lg:hidden fixed inset-0 z-50 animate-fade-in">
          <div className="absolute inset-0 bg-ink-950/60 backdrop-blur-sm" onClick={() => setMenuMobile(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-[272px] shadow-modal">
            {sidebar}
          </aside>
        </div>
      )}

      {/* Conteúdo */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 shrink-0 bg-white/80 backdrop-blur-md border-b border-ink-200/70 flex items-center gap-4 px-4 sm:px-6 lg:px-8 no-print">
          <button
            onClick={() => setMenuMobile(true)}
            className="lg:hidden icon-btn"
            aria-label="Abrir menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h2 className="font-display text-[15px] font-semibold text-ink-900 tracking-tight truncate">
            {tituloPagina}
          </h2>
          <div className="ml-auto flex items-center gap-3">
            <p className="hidden md:block text-[13px] text-ink-400 capitalize">
              {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <Link to="/pedidos/novo" className="btn-primary !py-2 !px-3.5 text-[13px]">
              <PackagePlus className="w-4 h-4" />
              <span className="hidden sm:inline">Novo Pedido</span>
            </Link>
          </div>
        </header>

        {/* Página */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1400px] mx-auto animate-fade-up">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
